import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import VitalSignsService, {
  PatientVitalSigns,
} from "../../../../services/vitalService";

interface VitalSignsHistoryProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  refreshTrigger?: number;
}

const VitalSignsHistory: React.FC<VitalSignsHistoryProps> = ({
  patient,
  isOpen,
  onClose,
  refreshTrigger,
}) => {
  const [vitalSigns, setVitalSigns] = useState<PatientVitalSigns[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: {
      delete?: boolean;
    };
  }>({});

  useEffect(() => {
    if (isOpen && patient.id) {
      loadVitalSigns();
    }
  }, [isOpen, patient.id, refreshTrigger]);

  const loadVitalSigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await VitalSignsService.getPatientVitalSigns(
        patient.id,
        20
      );
      setVitalSigns(result);
    } catch (err: any) {
      setError(err.message || "Failed to load vital signs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (vitalSignsId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this vital signs record?"
      )
    ) {
      return;
    }

    setActionLoading((prev) => ({
      ...prev,
      [vitalSignsId]: { ...prev[vitalSignsId], delete: true },
    }));

    try {
      await VitalSignsService.deleteVitalSigns(vitalSignsId);
      setVitalSigns((prev) => prev.filter((vs) => vs.id !== vitalSignsId));
    } catch (err: any) {
      alert(`Failed to delete vital signs record: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [vitalSignsId]: { ...prev[vitalSignsId], delete: false },
      }));
    }
  };

  const getStatusBadge = (status: string, color: string) => (
    <span
      style={{
        padding: "4px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: "500",
        background: color + "20",
        color: color,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {VitalSignsService.getStatusIcon(status)} {status}
    </span>
  );

  // Simple conditional rendering - just like TreatmentPlanHistory
  if (!isOpen) return null;

  // Always show inline mode (no modal)
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "16px",
        marginTop: "24px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e5e7eb",
        maxHeight: "600px",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <h4
          style={{
            fontSize: "16px",
            fontWeight: "600",
            margin: 0,
            marginBottom: "4px",
            color: "#1f2937",
          }}
        >
          🩺 Vital Signs History
        </h4>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          All vital signs records for{" "}
          {patient.full_name || `${patient.first_name} ${patient.last_name}`}
        </p>
      </div>

      {/* Content */}
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          Loading vital signs...
        </div>
      ) : error ? (
        <div
          style={{
            textAlign: "center",
            color: "#ef4444",
            padding: "40px 16px",
            fontSize: "16px",
          }}
        >
          ⚠️ {error}
          <br />
          <button
            onClick={loadVitalSigns}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: "#3b82f6",
              color: "white",
              marginTop: "16px",
            }}
          >
            Retry
          </button>
        </div>
      ) : vitalSigns.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#6b7280",
            padding: "40px 16px",
            fontSize: "16px",
          }}
        >
          🩺 No vital signs records found for this patient
        </div>
      ) : (
        <div>
          {/* Vital Signs List */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              maxHeight: "100%",
              overflowY: "auto",
              paddingRight: "4px",
              scrollbarWidth: "thin",
            }}
          >
            {vitalSigns.map((vitalSign) => (
              <div
                key={vitalSign.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "box-shadow 0.2s ease",
                  marginBottom: "8px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0, 0, 0, 0.1)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                {/* Vital Signs Header */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          margin: 0,
                          color: "#1f2937",
                        }}
                      >
                        🩺 Vital Signs Record
                      </h3>
                      {vitalSign.assessments?.overall &&
                        getStatusBadge(
                          vitalSign.assessments.overall.status,
                          vitalSign.assessments.overall.color
                        )}
                      {vitalSign.requires_attention && (
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "500",
                            background: "#fecaca",
                            color: "#dc2626",
                            whiteSpace: "nowrap",
                          }}
                        >
                          🚨 Attention Required
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: "8px",
                        fontSize: "14px",
                        color: "#6b7280",
                      }}
                    >
                      <div>
                        <strong>Measured:</strong>{" "}
                        {vitalSign.measurement_info?.measured_at_formatted ||
                          (vitalSign.measurement_info?.measured_at
                            ? VitalSignsService.formatDateTime(
                                vitalSign.measurement_info.measured_at
                              )
                            : "Unknown date")}
                      </div>
                      <div>
                        <strong>By:</strong>{" "}
                        {vitalSign.measurement_info?.measured_by?.name ||
                          "Unknown"}
                      </div>
                      {vitalSign.time_since_measurement && (
                        <div>
                          <strong>Time ago:</strong>{" "}
                          {vitalSign.time_since_measurement}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <button
                      onClick={() => handleDelete(vitalSign.id!)}
                      disabled={actionLoading[vitalSign.id!]?.delete}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        border: "none",
                        fontSize: "12px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background: "#ef4444",
                        color: "white",
                        minWidth: "100px",
                        opacity: actionLoading[vitalSign.id!]?.delete ? 0.7 : 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {actionLoading[vitalSign.id!]?.delete
                        ? "🗑️ Deleting..."
                        : "🗑️ Delete"}
                    </button>
                  </div>
                </div>

                {/* Vital Signs Details */}
                <div style={{ padding: "16px" }}>
                  {/* Vital Signs Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    {/* Blood Pressure */}
                    {(vitalSign.vital_signs?.systolic_bp ||
                      vitalSign.vital_signs?.diastolic_bp) && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          🫀 Blood Pressure
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color:
                              vitalSign.assessments?.blood_pressure?.color ||
                              "#1f2937",
                            wordBreak: "break-word",
                          }}
                        >
                          {vitalSign.display_values?.blood_pressure ||
                            `${vitalSign.vital_signs?.systolic_bp || "--"}/${
                              vitalSign.vital_signs?.diastolic_bp || "--"
                            } mmHg`}
                        </div>
                        {vitalSign.assessments?.blood_pressure?.status && (
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>
                            {vitalSign.assessments.blood_pressure.status}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Heart Rate */}
                    {vitalSign.vital_signs?.heart_rate && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          💓 Heart Rate
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color:
                              vitalSign.assessments?.heart_rate?.color ||
                              "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.heart_rate ||
                            `${vitalSign.vital_signs.heart_rate} bpm`}
                        </div>
                        {vitalSign.assessments?.heart_rate?.status && (
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>
                            {vitalSign.assessments.heart_rate.status}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Temperature */}
                    {vitalSign.vital_signs?.temperature && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          🌡️ Temperature
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color:
                              vitalSign.assessments?.temperature?.color ||
                              "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.temperature ||
                            `${vitalSign.vital_signs.temperature}°C`}
                        </div>
                        {vitalSign.assessments?.temperature?.status && (
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>
                            {vitalSign.assessments.temperature.status}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Oxygen Saturation */}
                    {vitalSign.vital_signs?.oxygen_saturation && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          🫁 Oxygen Saturation
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color:
                              vitalSign.assessments?.oxygen_saturation?.color ||
                              "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.oxygen_saturation ||
                            `${vitalSign.vital_signs.oxygen_saturation}%`}
                        </div>
                        {vitalSign.assessments?.oxygen_saturation?.status && (
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>
                            {vitalSign.assessments.oxygen_saturation.status}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Respiratory Rate */}
                    {vitalSign.vital_signs?.respiratory_rate && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          🫁 Respiratory Rate
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.respiratory_rate ||
                            `${vitalSign.vital_signs.respiratory_rate} breaths/min`}
                        </div>
                      </div>
                    )}

                    {/* Weight */}
                    {vitalSign.vital_signs?.weight && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          ⚖️ Weight
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.weight ||
                            `${vitalSign.vital_signs.weight} kg`}
                        </div>
                        {vitalSign.vital_signs?.bmi && (
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>
                            BMI: {vitalSign.vital_signs.bmi} kg/m² (
                            {vitalSign.assessments?.bmi?.category || "Unknown"})
                          </div>
                        )}
                      </div>
                    )}

                    {/* Height */}
                    {vitalSign.vital_signs?.height && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          minWidth: "0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          📏 Height
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                          }}
                        >
                          {vitalSign.display_values?.height ||
                            `${vitalSign.vital_signs.height} cm`}
                        </div>
                      </div>
                    )}

                    {/* Pain Scale */}
                    {vitalSign.vital_signs?.pain_scale !== undefined &&
                      vitalSign.vital_signs?.pain_scale !== null && (
                        <div
                          style={{
                            background: "#f9fafb",
                            border: "1px solid #f3f4f6",
                            borderRadius: "6px",
                            padding: "12px",
                            minWidth: "0",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            😣 Pain Level
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color:
                                vitalSign.assessments?.pain?.color || "#1f2937",
                            }}
                          >
                            {vitalSign.vital_signs.pain_scale}/10
                          </div>
                          {vitalSign.assessments?.pain?.level && (
                            <div style={{ fontSize: "11px", color: "#6b7280" }}>
                              {vitalSign.assessments.pain.level}
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  {/* Alerts */}
                  {vitalSign.alerts && vitalSign.alerts.length > 0 && (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          marginBottom: "8px",
                          color: "#dc2626",
                        }}
                      >
                        🚨 Alerts:
                      </div>
                      {vitalSign.alerts.map((alert, index) => (
                        <div
                          key={index}
                          style={{
                            fontSize: "12px",
                            color: "#dc2626",
                            marginBottom: "4px",
                            wordBreak: "break-word",
                          }}
                        >
                          • {alert.message} ({alert.value})
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Notes */}
                  {vitalSign.notes && (
                    <div
                      style={{
                        background: "#f0f9ff",
                        border: "1px solid #dbeafe",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          marginBottom: "4px",
                          color: "#1e40af",
                        }}
                      >
                        Diagonosis:
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#1e40af",
                          wordBreak: "break-word",
                        }}
                      >
                        {vitalSign.notes}
                      </div>
                    </div>
                  )}

                  {/* Timestamp Information */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      fontSize: "12px",
                      color: "#6b7280",
                      paddingTop: "12px",
                      borderTop: "1px solid #f3f4f6",
                    }}
                  >
                    <div>
                      Recorded:{" "}
                      {vitalSign.created_at
                        ? VitalSignsService.formatDateTime(vitalSign.created_at)
                        : "Unknown time"}
                    </div>
                    {vitalSign.updated_at !== vitalSign.created_at &&
                      vitalSign.updated_at && (
                        <div>
                          Updated:{" "}
                          {VitalSignsService.formatDateTime(
                            vitalSign.updated_at
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div
            style={{
              marginTop: "24px",
              padding: "16px",
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              textAlign: "center",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <strong>Total:</strong> {vitalSigns.length}
              </div>
              <div>
                <strong>Normal:</strong>{" "}
                {
                  vitalSigns.filter(
                    (vs) => vs.assessments?.overall?.status === "Normal"
                  ).length
                }
              </div>
              <div>
                <strong>Abnormal:</strong>{" "}
                {vitalSigns.filter((vs) => vs.has_abnormal_values).length}
              </div>
              <div>
                <strong>Critical:</strong>{" "}
                {
                  vitalSigns.filter(
                    (vs) => vs.assessments?.overall?.status === "Critical"
                  ).length
                }
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          /* Custom scrollbar styling */
          div::-webkit-scrollbar {
            width: 6px;
          }
          
          div::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          div::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          div::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          @media (max-width: 768px) {
            .vital-grid {
              grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            }
            
            .record-header {
              flex-direction: column;
              align-items: stretch;
            }
            
            .status-badges {
              flex-wrap: wrap;
              gap: 8px;
            }
          }
          
          @media (max-width: 480px) {
            .vital-grid {
              grid-template-columns: 1fr;
            }
            
            .summary-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .timestamp-info {
              flex-direction: column;
              align-items: flex-start;
            }
          }
        `}
      </style>
    </div>
  );
};

export default VitalSignsHistory;
